import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import { ThemeService } from '@ld.consumer-portal/utils';

function hasLetterValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  return /^(?=.*[a-zA-Z])/.test(control.value) ? null : { hasLetter: true };
}

function hasDigitValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  return /^(?=.*\d)/.test(control.value) ? null : { hasDigit: true };
}

function hasSpecialCharValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  return /[^a-zA-Z0-9\s]/.test(control.value) ? null : { hasSpecialChar: true };
}

function noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  return /^\S+$/.test(control.value) ? null : { hasWhitespace: true };
}

function matchValueValidator(matchControl: string) {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent) return null;
    const matchValue = control.parent.get(matchControl)?.value;
    if (!control.value || !matchValue) return null;
    return control.value === matchValue ? null : { matchValue: true };
  };
}

@Component({
  selector: 'ld-reset-password-card',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslocoDirective,
  ],
  templateUrl: './reset-password-card.component.html',
  styleUrl: './reset-password-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordCardComponent {
  private readonly fb = inject(FormBuilder);
  private readonly themeService = inject(ThemeService);

  protected logoUrl = computed(() => this.themeService.getLogoUrl());
  protected partnerDisplayName = computed(() => this.themeService.getPartnerDisplayName());

  isLoading = input(false);
  externalErrorMessage = input<string | null>(null);

  submitPassword = output<{ newPassword: string }>();
  backToLogin = output<void>();

  hideNewPassword = signal(true);
  hideVerifyPassword = signal(true);
  loading = computed(() => this.isLoading());
  errorMessage = computed(() => this.externalErrorMessage());

  private readonly newPasswordValue = signal('');
  private readonly verifyPasswordValue = signal('');

  resetPasswordForm = this.fb.group({
    newPassword: [
      '',
      [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(256),
        hasLetterValidator,
        hasDigitValidator,
        hasSpecialCharValidator,
        noWhitespaceValidator,
      ],
    ],
    verifyPassword: ['', [Validators.required, matchValueValidator('newPassword')]],
  });

  protected passwordValidationRules = computed(() => {
    const value = this.newPasswordValue();

    return [
      { key: 'hasLetter', label: 'authResetPasswordValidationHasLetter', valid: /^(?=.*[a-zA-Z])/.test(value) },
      { key: 'hasNumber', label: 'authResetPasswordValidationHasNumber', valid: /^(?=.*\d)/.test(value) },
      { key: 'hasSymbol', label: 'authResetPasswordValidationHasSymbol', valid: /[^a-zA-Z0-9\s]/.test(value) },
      { key: 'minLength', label: 'authResetPasswordValidationMinLength', valid: /.{8,}/.test(value) },
      { key: 'noSpaces', label: 'authResetPasswordValidationNoSpaces', valid: value.length > 0 && /^\S+$/.test(value) },
    ];
  });

  protected passwordsMatch = computed(() => {
    const newPassword = this.newPasswordValue();
    const verifyPassword = this.verifyPasswordValue();
    return newPassword.length > 0 && verifyPassword.length > 0 && newPassword === verifyPassword;
  });

  protected toggleNewPasswordVisibility(): void {
    this.hideNewPassword.update((hide) => !hide);
  }

  protected toggleVerifyPasswordVisibility(): void {
    this.hideVerifyPassword.update((hide) => !hide);
  }

  protected onNewPasswordInput(): void {
    this.newPasswordValue.set(this.resetPasswordForm.get('newPassword')?.value ?? '');
    const verifyControl = this.resetPasswordForm.get('verifyPassword');
    if (verifyControl?.value) {
      verifyControl.updateValueAndValidity();
    }
  }

  protected onVerifyPasswordInput(): void {
    this.verifyPasswordValue.set(this.resetPasswordForm.get('verifyPassword')?.value ?? '');
  }

  protected onSubmit(): void {
    this.resetPasswordForm.markAllAsTouched();
    this.resetPasswordForm.updateValueAndValidity();
    this.resetPasswordForm.get('verifyPassword')?.updateValueAndValidity();

    if (this.resetPasswordForm.invalid) {
      return;
    }

    const newPassword = this.resetPasswordForm.get('newPassword')?.value ?? '';
    this.submitPassword.emit({ newPassword });
  }

  protected onBackToLogin(): void {
    this.backToLogin.emit();
  }
}
